import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {NolimitPlugin} from "@nolimitcity/slot-launcher/bin/NolimitPlugin";
import {Logger, LogLevel} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {GameFlowController} from "@nolimitcity/slot-game/bin/flowcontrol/GameFlowController";
import {FlowState} from "@nolimitcity/slot-game/bin/flowcontrol/FlowState";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FontLoader} from "@nolimitcity/slot-launcher/bin/loader/FontLoader";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {AssetsConfig} from "@nolimitcity/slot-game/bin/asset/AssetsConfig";
import {AssetLoader} from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/asset/ResourcesGroupName";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import {Speedster} from "./debug/Speedster";
import {TheCryptGameAssets} from "./TheCryptGameAssets";
import {TheCryptFreespinIntro} from "./view/TheCryptFreespinIntro";
import {TheCryptFreespinOutro} from "./view/TheCryptFreespinOutro";
import {TheCryptWinCapView} from "./view/TheCryptWinCapView";
import {TheCryptSoundManager} from "./sound/TheCryptSoundManager";
import {TheCryptBackground} from "./view/TheCryptBackground";
import {TheCryptHelper} from "./utils/TheCryptHelper";
import {TheCryptModel} from "./server/TheCryptModel";
import {TheCryptAvalancheReelModel} from "./reels/TheCryptAvalancheReelModel";
import {TheCryptGameConfig} from "./TheCryptGameConfig";
import {TheCryptReelArea} from "./reels/TheCryptReelArea";
import {TheCryptIdleNormalPresentation} from "./gameflow/TheCryptIdleNormalPresentation";
import {TheCryptNormalBetPresentation} from "./gameflow/TheCryptNormalBetPresentation";
import {TheCryptFSBetPresentation} from "./gameflow/freespin/TheCryptFSBetPresentation";
import {TheCryptFSResultPresentation} from "./gameflow/freespin/TheCryptFSResultPresentation";
import {TheCryptNormalResultPresentation} from "./gameflow/TheCryptNormalResultPresentation";
import {TheCryptFSWinPresentation} from "./gameflow/freespin/TheCryptFSWinPresentation";
import {TheCryptFSIdlePresentation} from "./gameflow/freespin/TheCryptFSIdlePresentation";
import {TheCryptAvalancheIdlePresentation} from "./gameflow/avalanche/TheCryptAvalancheIdlePresentation";
import {TheCryptGameMode} from "./server/TheCryptGameMode";
import {ISymbolValues} from "./server/ITheCryptData";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TheCryptCollector} from "./view/TheCryptCollector";
import {TheCryptWinPresentation} from "./gameflow/TheCryptWinPresentation";
import {TheCryptInitialWinView} from "./view/TheCryptInitialWinView";
import {TheCryptTotalWinView} from "./view/TheCryptTotalWinView";
import {TheCryptBigWinView} from "./view/TheCryptBigWinView";
import {TheCryptGameView} from "./view/TheCryptGameView";
import {TheCryptReelFrame} from "./view/TheCryptReelFrame";
import {TheCryptReelBackground} from "./view/TheCryptReelBackground";
import {TheCryptSymbolAnimation} from "./view/TheCryptSymbolAnimation";
import {ExtraFreespinAwardView} from "./view/ExtraFreespinAwardView";
import {HighSymbolReelArea} from "./view/HighSymbolReelArea";
import {TheCryptReelAnticipation} from "./view/TheCryptReelAnticipation";
import {TheCryptFullScreenDimmer} from "./view/TheCryptFullScreenDimmer";
import {Skinner} from "./Skinner";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class TheCrypt extends SlotGame {
    public static boostedName: string = "BOOSTED_BET";
    public static model: TheCryptModel;
    public static gameFlowController: GameFlowController;
    public static assetLoader: AssetLoader;
    public static reels: TheCryptAvalancheReelModel[];
    public static freespinIntro: TheCryptFreespinIntro;
    public static freespinOutro: TheCryptFreespinOutro;
    public static winCapScreen: TheCryptWinCapView;
    public static realArea: TheCryptReelArea;
    public static background: TheCryptBackground;
    public static collectors: TheCryptCollector;
    public static initialWinView: TheCryptInitialWinView;
    public static totalWinView: TheCryptTotalWinView;
    public static bigWinView: TheCryptBigWinView;
    public static extraFreespinAwardView: ExtraFreespinAwardView;
    public static gameViewContainer: TheCryptGameView;
    public static reelFrame: TheCryptReelFrame;
    public static reelBackground: TheCryptReelBackground;
    public static symbolAnimation: TheCryptSymbolAnimation;
    public static emptyLayerBelowCollector: PIXI.Container;
    public static fullScreenDimmer: PIXI.Container;
    public static highSymbolRealArea: HighSymbolReelArea;
    public static reelAnticipation: TheCryptReelAnticipation;
    public static promoPanelOpened: boolean;
    private _defaultSymbolValues: ISymbolValues = {
        "L1": {3: 3, 4: 4, 5: 8, 6: 15},
        "L2": {3: 3, 4: 4, 5: 8, 6: 14},
        "L3": {3: 3, 4: 4, 5: 8, 6: 12},
        "L4": {3: 2, 4: 3, 5: 6, 6: 12},
        "L5": {3: 2, 4: 3, 5: 6, 6: 10},
        "L6": {3: 2, 4: 3, 5: 6, 6: 10},
        "M1": {3: 10, 4: 25, 5: 50, 6: 75},
        "M2": {3: 5, 4: 8, 5: 20, 6: 30},
        "M3": {3: 4, 4: 6, 5: 15, 6: 25},
        "M4": {3: 4, 4: 6, 5: 12, 6: 22},
        "M5": {3: 4, 4: 5, 5: 10, 6: 20},
        "M6": {3: 4, 4: 5, 5: 10, 6: 18},
        "W": {3: 10, 4: 25, 5: 50, 6: 75}
    }
    private skinner: Skinner;

    constructor() {
        super();
        const validSkinIds: string[] = [
            "ug",
            "ozzo",
            "test",
            "sweep"
        ]
        this.skinner = new Skinner(validSkinIds);
    }

    private static addLayer(layer: any): any {
        NolimitApplication.addLayerAt(layer.name, layer, 0);
        return layer;
    }

    private static createViewsAndComponents() {
        if (NolimitConfig.isDevMode) {
            NolimitApplication.addLayerAt("SPEEDSTER", new PIXI.Container(), 0);
            TheCrypt.addLayer(new Speedster());
        }

        TheCrypt.symbolAnimation = new TheCryptSymbolAnimation();
        TheCrypt.highSymbolRealArea = new HighSymbolReelArea();
        TheCrypt.realArea = new TheCryptReelArea();

        TheCrypt.background = new TheCryptBackground();
        TheCrypt.gameViewContainer = new TheCryptGameView();
        TheCrypt.reelFrame = new TheCryptReelFrame();
        TheCrypt.reelBackground = new TheCryptReelBackground();
        TheCrypt.collectors = new TheCryptCollector();
        TheCrypt.initialWinView = new TheCryptInitialWinView();
        TheCrypt.emptyLayerBelowCollector = new PIXI.Container();
        TheCrypt.reelAnticipation = new TheCryptReelAnticipation();

        TheCrypt.gameViewContainer.addChild(TheCrypt.background);
        TheCrypt.gameViewContainer.addChild(TheCrypt.reelBackground);
        TheCrypt.gameViewContainer.addChild(TheCrypt.realArea);
        TheCrypt.gameViewContainer.addChild(TheCrypt.highSymbolRealArea);
        TheCrypt.gameViewContainer.addChild(TheCrypt.reelFrame);
        TheCrypt.gameViewContainer.addChild(TheCrypt.symbolAnimation);
        TheCrypt.gameViewContainer.addChild(TheCrypt.reelAnticipation);
        TheCrypt.gameViewContainer.addChild(TheCrypt.emptyLayerBelowCollector);
        TheCrypt.gameViewContainer.addChild(TheCrypt.collectors);
        TheCrypt.gameViewContainer.addChild(TheCrypt.initialWinView);

        NolimitApplication.addLayerAt(TheCrypt.gameViewContainer.name, TheCrypt.gameViewContainer, 0);

        TheCrypt.fullScreenDimmer = new TheCryptFullScreenDimmer();
        NolimitApplication.addLayerAt(TheCrypt.fullScreenDimmer.name, TheCrypt.fullScreenDimmer, 2);

        TheCrypt.totalWinView = new TheCryptTotalWinView();
        NolimitApplication.addLayerAt(TheCrypt.totalWinView.name, TheCrypt.totalWinView, 3);

        TheCrypt.bigWinView = new TheCryptBigWinView();
        NolimitApplication.addLayerAt(TheCrypt.bigWinView.name, TheCrypt.bigWinView, 4);

        TheCrypt.extraFreespinAwardView = new ExtraFreespinAwardView();
        NolimitApplication.addLayerAt(TheCrypt.extraFreespinAwardView.name, TheCrypt.extraFreespinAwardView, 5);

        TheCrypt.freespinIntro = new TheCryptFreespinIntro();
        NolimitApplication.addLayerAt(TheCrypt.freespinIntro.name, TheCrypt.freespinIntro, 6);

        TheCrypt.freespinOutro = new TheCryptFreespinOutro();
        NolimitApplication.addLayerAt(TheCrypt.freespinOutro.name, TheCrypt.freespinOutro, 7);

        TheCrypt.winCapScreen = new TheCryptWinCapView();
        NolimitApplication.addLayerAt(TheCrypt.winCapScreen.name, TheCrypt.winCapScreen, 8);


        TheCrypt.reels = [];
        for (let i: number = 0; i < TheCryptGameConfig.reel_num; i++) {
            const reel = new TheCryptAvalancheReelModel(i);
            TheCrypt.reels.push(reel);
        }
    }

    private static getSymbolString(key: string, data: { [numSymbols: number]: number }): string {
        return `
            <div class="symbol">
            <img src="{{gamePath}}screens/images/symbols/${key}.png">
            <div class="payout-group">
            
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">6</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[6] * 0.05}">${data[6] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">5</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[5] * 0.05}">${data[5] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">4</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[4] * 0.05}">${data[4] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">3</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[3] * 0.05}">${data[3] * 0.05}</span>
                </div>
            </div>
            </div>
        `;
    }

    public static setMode(mode: string): void {
        TheCrypt.background.setMode(mode);
        TheCrypt.reelFrame.setMode(mode);
        TheCrypt.reelBackground.setMode(mode);
    }

    public init(): Promise<NolimitPlugin> {
        return super.init().then((value: NolimitPlugin) => {
            if (NolimitConfig.isDevMode) {
                (<any>globalThis).__PIXI_APP__ = NolimitApplication.pixiApp;
            }
            TheCrypt.model = new TheCryptModel(SlotGame.apiPlugin, true);
            TheCrypt.gameFlowController = new GameFlowController(SlotGame.apiPlugin, TheCrypt.model);
            TheCrypt.gameFlowController.flowStateLogger.level = LogLevel.DEV;

            const winPres = new TheCryptWinPresentation();
            const normalBet = new TheCryptNormalBetPresentation();
            const normalResult = new TheCryptNormalResultPresentation();


            TheCrypt.gameFlowController.addGameModeFlowStatePresentationMap(GameMode.NORMAL, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, new TheCryptIdleNormalPresentation()],
                [FlowState.BET, normalBet],
                [FlowState.RESULT, normalResult],
                [FlowState.WIN, winPres]
            ]));
            TheCrypt.gameFlowController.addGameModeFlowStatePresentationMap(TheCryptGameMode.NORMAL_AVALANCHE, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, new TheCryptAvalancheIdlePresentation()],
                [FlowState.RESULT, normalResult],
                [FlowState.WIN, winPres]
            ]));

            TheCrypt.gameFlowController.addGameModeFlowStatePresentationMap(GameMode.FREESPIN, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, new TheCryptFSIdlePresentation()],
                [FlowState.BET, new TheCryptFSBetPresentation()],
                [FlowState.RESULT, new TheCryptFSResultPresentation()],
                [FlowState.WIN, new TheCryptFSWinPresentation()]
            ]));

            TheCrypt.gameFlowController.addGameModeFlowStatePresentationMap(TheCryptGameMode.FREESPIN_AVALANCHE, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, new TheCryptFSIdlePresentation()],
                [FlowState.BET, new TheCryptFSBetPresentation()],
                [FlowState.RESULT, new TheCryptFSResultPresentation()],
                [FlowState.WIN, new TheCryptFSWinPresentation()]
            ]));
            this.handleSweepInit();
            return value;
        });
    }

    public async getReady(): Promise<NolimitPlugin> {
        //Show intro and load game assets
        const value = await super.getReady();
        const fontLoader = new FontLoader(NolimitApplication.resourcePath);
        fontLoader.add(OpenSans.ITALIC_600);
        fontLoader.add(OpenSans.NORMAL_300);
        fontLoader.add(OpenSans.NORMAL_400);
        fontLoader.add(OpenSans.NORMAL_700);

        AssetsConfig.addToAssetsConfig(TheCryptGameAssets.RESOURCES_GROUPS);
        TheCrypt.assetLoader = new AssetLoader(TheCrypt.apiPlugin, NolimitApplication.resourcePath + "/resources/");

        await Promise.all([fontLoader.load(), TheCrypt.assetLoader.load(ResourcesGroupName.MAIN)]);


        if (this.skinner.skinId != undefined) {
            //Source folder for skin assets e.g "resources/images/skinName";
            const assetPath: string = "resources/images/";
            const replaceMap: Map<string, string> = new Map();

            if (this.skinner.skinId === "sweep") {
                replaceMap.set("BOOSTED_BET", "BOOSTED_BET.png");
                replaceMap.set("pageImage_6", "pageImage_6.png");
            } else {
                //Intro
                replaceMap.set("logo", "introLogo.png");
                replaceMap.set("feature1", "intro_feature1.png");

                //Game
                replaceMap.set("S/parts/moon", "moon.png");
                replaceMap.set("S/parts/moon_red", "moon_red.png");
                replaceMap.set("S/S_1_spin", "S_1_spin.png");
                replaceMap.set("S/S_2_spin", "S_2_spin.png");
                replaceMap.set("S/S_3_spin", "S_3_spin.png");

                //Bonus
                replaceMap.set("FREESPIN_3_SCATTERS", "FREESPIN_3_SCATTERS.png");
                replaceMap.set("FREESPIN_4_SCATTERS", "FREESPIN_4_SCATTERS.png");
                replaceMap.set("FREESPIN_5_SCATTERS", "FREESPIN_5_SCATTERS.png");

                //Action spins
                replaceMap.set("AS_GAME_LOGO", "GAME_LOGO.png");

                //pageImage_3
            }
            await this.skinner.loadSkin(assetPath, replaceMap)
        }

        await TheCrypt.createViewsAndComponents();

        return value;
    }

    public async getReadyToStart(): Promise<NolimitPlugin> {
        //Game assets loaded, showing intro, waiting for user to start game.
        if (TheCrypt.model.isRestore) {
            TheCrypt.keypadPlugin.setWin(TheCrypt.model.initData.accumulatedRoundWin, true, TheCryptHelper.isWinBelowStake());
        }
        return await super.getReadyToStart();
    }

    public async start(): Promise<NolimitPlugin> {
        //User has pressed continue and the game starts.
        TheCryptSoundManager.playAmbientLoop(1000);
        const value = await super.start();
        TheCrypt.gameFlowController.start();
        TheCrypt.setMode(TheCrypt.model.mode);

        return value;
    }

    public getPaytable(): Promise<string> {
        let symbolDataValueData: ISymbolValues = this._defaultSymbolValues;
        if (TheCrypt.model.initData.symbolValues) {
            symbolDataValueData = TheCrypt.model.initData.symbolValues;
            Logger.logDev("Using symbolValues from INIT data");
        } else {
            Logger.logDev("Using default symbolValues");
        }

        const data = {
            imagePath: "screens/images/",
            sweepPath: "screens/images/",
        };

        if (this.skinner.skinId != undefined) {
            if (this.skinner.skinId === "sweep") {
                data.sweepPath = `../resources/images/${this.skinner.skinId}/`;
            } else {
                data.imagePath = `../resources/images/${this.skinner.skinId}/`;
            }
        }
        return super.getPaytable().then((value: string) => {
            value = value.replace(/{{sweepPath}}/g, data.sweepPath);
            value = value.replace(/{{imagePath}}/g, data.imagePath);

            for (let key in symbolDataValueData) {
                value = value.replace(`{{${key}}}`, TheCrypt.getSymbolString(key, symbolDataValueData[key]))
            }
            return value;
        });
    }

    getNoWinGameData(): any {
        return {
            "reels": [
                [
                    "Y",
                    "Y",
                    "L2",
                    "L2",
                    "M6",
                    "L1"
                ],
                [
                    "Y",
                    "Y",
                    "M1",
                    "M5",
                    "L4",
                    "L4"
                ],
                [
                    "Y",
                    "Y",
                    "M4",
                    "L3",
                    "L1",
                    "L1"
                ],
                [
                    "Y",
                    "Y",
                    "L5",
                    "M6",
                    "L3",
                    "S"
                ],
                [
                    "Y",
                    "Y",
                    "M6",
                    "L3",
                    "L3",
                    "M5"
                ],
                [
                    "Y",
                    "Y",
                    "L1",
                    "L5",
                    "M1",
                    "L4"
                ]
            ],
            "evaluatedArea": [
                [
                    "Y",
                    "Y",
                    "L2",
                    "L2",
                    "M6",
                    "L1"
                ],
                [
                    "Y",
                    "Y",
                    "M1",
                    "M5",
                    "L4",
                    "L4"
                ],
                [
                    "Y",
                    "Y",
                    "M4",
                    "L3",
                    "L1",
                    "L1"
                ],
                [
                    "Y",
                    "Y",
                    "L5",
                    "M6",
                    "L3",
                    "S"
                ],
                [
                    "Y",
                    "Y",
                    "M6",
                    "L3",
                    "L3",
                    "M5"
                ],
                [
                    "Y",
                    "Y",
                    "L1",
                    "L5",
                    "M1",
                    "L4"
                ]
            ],
            "reelsAfterCollapse": [
                [
                    "Y",
                    "Y",
                    "L2",
                    "L2",
                    "M6",
                    "L1"
                ],
                [
                    "Y",
                    "Y",
                    "M1",
                    "M5",
                    "L4",
                    "L4"
                ],
                [
                    "Y",
                    "Y",
                    "M4",
                    "L3",
                    "L1",
                    "L1"
                ],
                [
                    "Y",
                    "Y",
                    "L5",
                    "M6",
                    "L3",
                    "S"
                ],
                [
                    "Y",
                    "Y",
                    "M6",
                    "L3",
                    "L3",
                    "M5"
                ],
                [
                    "Y",
                    "Y",
                    "L1",
                    "L5",
                    "M1",
                    "L4"
                ]
            ],
            "betWayWins": [],
            "totalSpinWinnings": 0,
            "accumulatedRoundWin": 0,
            "playedBetValue": 100,
            "mode": "NORMAL",
            "nextMode": "NORMAL",
            "numberOfFreespinsPlayed": 0,
            "freespinsLeft": 0,
            "addedNumberOfFreespinsThisSpin": 0,
            "wasFeatureBuy": false,
            "brokeTheBank": false,
            "totalNumberOfWays": 4096,
            "dragonSideReel": [
                0,
                0
            ],
            "gameAreaSize": [
                4,
                4,
                4,
                4,
                4,
                4
            ],
            "xNudgeMultiplier": [
                0,
                0,
                0,
                0,
                0,
                0
            ],
            "thisSpinWinning": 0,
            "xNudgeMultiplierAfterCollapse": [
                0,
                0,
                0,
                0,
                0,
                0
            ],
            "m1NextMultiplier": 1,
            "waysWonTotalRound": 0,
            "freeSpinType": 0,
            "mysterySymbolRevealed": "",
            "m1CurrentMultiplier": 1,
            "level": 0,
            "numXNudge": 0,
            "position": [
                [
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false
                ]
            ]
        };
    }

    private handleSweepInit() {
        //Making sure that skinID is set based  on "replaceWordFree" gcc.
        if (SlotGame.apiPlugin.gameClientConfiguration.isSet) {
            if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                this.skinner.skinId = "sweep";
            }
        } else {
            SlotGame.apiPlugin.events.on(APIEvent.GAME_CLIENT_CONFIGURATION_APPLIED, () => {
                if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                    this.skinner.skinId = "sweep";
                }
            })
        }
    }
}